import { computePublicResourceBaseURL, normalizeResourceURL, request } from '../utils/request';
import { cache } from '../utils/cache';

const CACHE_TTL_SHORT = 10000;

const lastSendAt: Record<string, number> = {};
const failureUntil: Record<string, number> = {};
const readingHistoryInFlight: Record<string, Promise<unknown> | null> = {};
const READING_HISTORY_TTL = 30000;

/**
 * 阅读进度写入成功后，清理前端阅读历史缓存，确保后续恢复优先拿到服务端新值。
 */
const invalidateReadingHistoryCache = () => {
  for (const key of cache.keys()) {
    if (/:reading-history$/.test(key)) {
      cache.delete(key);
    }
  }

  for (const userId of Object.keys(readingHistoryInFlight)) {
    delete readingHistoryInFlight[userId];
  }
};

/**
 * 获取书籍详情
 */
export const getBookById = async (bookId: string) => {
  const ck = `book:${bookId}:detail`;
  const cached = cache.get<{
    id: string;
    title: string;
    author: string;
    format: string;
    file_path: string;
    cover_path?: string;
    description?: string;
    category?: string;
    tags?: string[];
    publisher?: string;
    publication_date?: string;
    edition?: string;
    printing?: string;
    isbn?: string;
    file_status?: string;
    status?: number;
    format_meta?: Record<string, unknown>;
  }>(ck);
  if (cached) return cached;
  const response = await request.get(`/api/books/${bookId}`, { headers: { 'X-Skip-401-Redirect': '1', 'X-No-Retry': '1' } });
  const payload = response.data?.data?.book;
  if (!payload) {
    throw new Error('无法获取书籍信息');
  }
  const result = {
    id: String(payload.id),
    title: payload.name,
    author: payload.author,
    format: payload.format,
    file_path: payload.file_path,
    cover_path: payload.preview_path || payload.cover_path,
    description: payload.text_abstract,
    category: payload.category || '',
    tags: Array.isArray(payload.tags) ? payload.tags : [],
    publisher: payload.publisher,
    publication_date: (() => {
      const v = payload.publication_date
      if (!v) return undefined
      const s = String(v)
      const m = s.match(/^\d{4}-\d{2}-\d{2}/)
      return m ? m[0] : s
    })(),
    edition: payload.edition,
    printing: payload.printing,
    isbn: payload.isbn,
    file_status: payload.file_status,
    status: typeof payload.status === 'number' ? payload.status : undefined,
    format_meta: (() => { try { return JSON.parse(payload.format_specific_meta || '{}') } catch { return {} } })(),
  };
  cache.set(ck, result, CACHE_TTL_SHORT);
  return result;
};

/**
 * 获取书籍内容（分页）
 */
export const getBookContent = async (bookId: string, params?: { offset?: number; limit?: number; refresh?: number }) => {
  const response = await request.get(`/api/books/${bookId}/content`, { params: params || { offset: 0, limit: 1000000 }, headers: { 'X-Skip-401-Redirect': '1', 'X-No-Retry': '1' } })
  return response.data?.data?.content as string
}

/**
 * 获取书籍EPUB预览地址（用于AZW3/MOBI精准阅读）
 */
export const getBookPreviewEpub = async (bookId: string, timeout?: number) => {
  const response = await request.get(`/api/books/${bookId}/preview/epub`, { 
    headers: { 'X-Skip-401-Redirect': '1', 'X-No-Retry': '1', 'X-Silent': '1' },
    timeout: timeout || 30000 
  })
  const url = String(response.data?.data?.url || '')
  const base = computePublicResourceBaseURL()
  try {
    const u = new URL(url, base)
    if (/^\/previews\//i.test(u.pathname)) return new URL(`${u.pathname}${u.search}${u.hash}`, base).toString()
  } catch { void 0 }
  return base ? normalizeResourceURL(url, base) : url
}

export const getBookPreviewEpubDir = async (bookId: string, timeout?: number) => {
  const response = await request.get(`/api/books/${bookId}/preview/epubdir`, {
    headers: { 'X-Skip-401-Redirect': '1', 'X-No-Retry': '1', 'X-Silent': '1' },
    timeout: timeout || 60000
  })
  const url = String(response.data?.data?.url || '')
  const base = computePublicResourceBaseURL()
  try {
    const u = new URL(url, base)
    if (/^\/previews\//i.test(u.pathname)) return new URL(`${u.pathname}${u.search}${u.hash}`, base).toString()
  } catch { void 0 }
  return base ? normalizeResourceURL(url, base) : url
}

/**
 * 获取书籍AZW3原生HTML预览地址
 */
export const getBookPreviewAzw3Html = async (bookId: string, timeout?: number) => {
  const response = await request.get(`/api/books/${bookId}/preview/azw3`, { 
    headers: { 'X-Skip-401-Redirect': '1', 'X-No-Retry': '1', 'X-Silent': '1' },
    timeout: timeout || 30000 
  })
  const url = String(response.data?.data?.url || '')
  const base = computePublicResourceBaseURL()
  try {
    const u = new URL(url, base)
    if (/^\/previews\//i.test(u.pathname)) return new URL(`${u.pathname}${u.search}${u.hash}`, base).toString()
  } catch { void 0 }
  return base ? normalizeResourceURL(url, base) : url
}

export const updateBookReadingProgress = async (bookId: string, progress: number) => {
  // 保持高精度，避免进度丢失
  const normalized = Math.max(0, Math.min(100, Number(Number(progress).toFixed(3))));
  const payload = {
    progress: normalized,
  };
  const now = Date.now();
  
  // 降低限流阈值，确保重要进度更新不被丢弃
  if (lastSendAt[bookId] && (now - lastSendAt[bookId]) < 1000) { // 从3秒降低到1秒
    return Promise.resolve({ success: true, throttled: true });
  }
  
  lastSendAt[bookId] = now;
  try {
    const response = await request.put(
      `/api/books/${bookId}/progress`,
      payload,
      { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } } // 移除X-No-Retry，允许重试
    );
    lastSendAt[bookId] = Date.now();
    // 成功时清除错误状态
    if (failureUntil[bookId]) {
      delete failureUntil[bookId];
    }
    invalidateReadingHistoryCache();
    return response.data;
  } catch (error) {
    // 缩短错误抑制时间，从60秒降到10秒
    failureUntil[bookId] = Date.now() + 10000;
    console.warn('更新阅读进度失败，但将继续尝试:', error);
    // 不throw error，避免中断进度恢复流程
    const errorMessage = error instanceof Error ? error.message : String(error);
    return { success: false, error: errorMessage };
  }
};

/**
 * 更新书籍阅读进度（别名）
 */
export const updateReadingProgress = updateBookReadingProgress;

/**
 * 获取用户阅读历史
 */
export const getReadingHistory = async (userId: string) => {
  const ck = `user:${userId}:reading-history`;
  const cached = cache.get<unknown>(ck);
  if (cached != null) return cached;
  const inFlight = readingHistoryInFlight[userId];
  if (inFlight) return inFlight;
  const task = (async () => {
    const response = await request.get(`/api/books/users/${userId}/reading-history`, {
      headers: { 'X-Skip-401-Redirect': '1', 'X-No-Retry': '1', 'X-Silent': '1' }
    });
    const data = response.data?.data;
    cache.set(ck, data, READING_HISTORY_TTL);
    return data;
  })();
  readingHistoryInFlight[userId] = task;
  try {
    return await task;
  } finally {
    readingHistoryInFlight[userId] = null;
  }
};

/**
 * 获取用户书签
 */
export const getUserBookmarks = async (userId: string) => {
  const ck = `user:${userId}:bookmarks`;
  const cached = cache.get<unknown>(ck);
  if (cached) return cached;
  const response = await request.get(`/api/users/${userId}/bookmarks`, { headers: { 'X-No-Retry': '1' } });
  const data = response.data;
  cache.set(ck, data, CACHE_TTL_SHORT);
  return data;
};

/**
 * 添加书签
 */
export const addBookmark = async (bookmark: {
  book_id: string;
  user_id: string;
  page: number;
  cfi?: string;
  title: string;
  note?: string;
}) => {
  const response = await request.post('/api/bookmarks', bookmark);
  return response.data;
};

/**
 * 删除书签
 */
export const deleteBookmark = async (bookmarkId: string) => {
  const response = await request.delete(`/api/bookmarks/${bookmarkId}`);
  return response.data;
};

/**
 * 获取用户笔记
 */
export const getUserNotes = async (userId: string) => {
  const ck = `user:${userId}:notes`;
  const cached = cache.get<unknown>(ck);
  if (cached) return cached;
  const response = await request.get(`/api/users/${userId}/notes`, { headers: { 'X-No-Retry': '1' } });
  const data = response.data;
  cache.set(ck, data, CACHE_TTL_SHORT);
  return data;
};

export const downloadUserNotes = async (userId: string) => {
  const url = `/api/users/${userId}/notes/export`;
  const response = await request.get(url, {
    responseType: 'blob',
    headers: { 'X-No-Retry': '1' }
  });
  return response;
};

/**
 * 添加笔记
 */
export const addNote = async (note: {
  book_id: string;
  user_id: string;
  page: number;
  cfi?: string;
  content: string;
  highlight?: string;
}) => {
  const response = await request.post('/api/notes', note);
  return response.data;
};

/**
 * 更新笔记
 */
export const updateNote = async (noteId: string, content: string) => {
  const response = await request.put(`/api/notes/${noteId}`, {
    content,
    updated_at: new Date().toISOString()
  });
  return response.data;
};

/**
 * 删除笔记
 */
export const deleteNote = async (noteId: string) => {
  const response = await request.delete(`/api/notes/${noteId}`);
  return response.data;
};

/**
 * 同步阅读进度到云端
 */
export const syncReadingProgress = async (progress: {
  user_id: string;
  book_id: string;
  progress: number;
  last_read_time: string;
}) => {
  const response = await request.post('/api/reading-progress/sync', progress);
  return response.data;
};

/**
 * 获取云端阅读进度
 */
export const getCloudReadingProgress = async (userId: string, bookId: string) => {
  const response = await request.get(`/api/reading-progress/${userId}/${bookId}`);
  return response.data;
};

/**
 * 获取推荐书籍
 */
export const getRecommendedBooks = async (userId: string, limit: number = 10) => {
  const response = await request.get(`/api/books/recommendations/user/${userId}?limit=${limit}`);
  return response.data;
};

export const getPublicRecommendations = async (limit: number = 20) => {
  const response = await request.get(`/api/books/recommendations/public?limit=${limit}`, { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } });
  return response.data?.data?.items || [];
};

export const rateBook = async (bookId: string, rating: number) => {
  const response = await request.post(`/api/books/${bookId}/rating`, { rating });
  try { cache.delete(`book:${bookId}:rating`) } catch { /* no-op */ }
  return response.data?.data;
};

export const getBookRating = async (bookId: string) => {
  const ck = `book:${bookId}:rating`;
  const cached = cache.get<unknown>(ck);
  if (cached) return cached;
  const response = await request.get(`/api/books/${bookId}/rating`, { headers: { 'X-Skip-401-Redirect': '1', 'X-No-Retry': '1' } });
  const data = response.data?.data;
  cache.set(ck, data, CACHE_TTL_SHORT);
  return data;
};

export const getMyBookRating = async (bookId: string) => {
  const response = await request.get(`/api/books/${bookId}/rating/me`, { headers: { 'X-Skip-401-Redirect': '1' } });
  return response.data?.data;
};

/**
 * 获取相似书籍
 */
export const getSimilarBooks = async (bookId: string, limit: number = 5) => {
  const response = await request.get(`/api/books/${bookId}/similar?limit=${limit}`);
  return response.data;
};

export type BookReviewItem = {
  id: number | string
  book_id: number | string
  user_id: number | string
  parent_id?: number | string | null
  username?: string
  content: string
  create_time?: string
  update_time?: string
  like_count?: number
  flower_count?: number
  downvote_count?: number
  dislike_count?: number
  reply_count?: number
  liked?: number | boolean
  flowered?: number | boolean
  downvoted?: number | boolean
  disliked?: number | boolean
}

export const getBookReviews = async (bookId: string, params?: { offset?: number; limit?: number }) => {
  const response = await request.get(`/api/books/${bookId}/reviews`, {
    params: params || { offset: 0, limit: 20 },
    headers: { 'X-Skip-401-Redirect': '1', 'X-No-Retry': '1' }
  })
  return (response.data?.data || response.data) as { items?: BookReviewItem[]; offset?: number; limit?: number }
}

export const getReviewReplies = async (reviewId: string, params?: { offset?: number; limit?: number }) => {
  const response = await request.get(`/api/reviews/${reviewId}/replies`, {
    params: params || { offset: 0, limit: 20 },
    headers: { 'X-Skip-401-Redirect': '1', 'X-No-Retry': '1' }
  })
  return (response.data?.data || response.data) as { items?: BookReviewItem[]; offset?: number; limit?: number }
}

export const createBookReview = async (bookId: string, payload: { content: string; parent_id?: number | string | null }) => {
  const response = await request.post(`/api/books/${bookId}/reviews`, payload, { headers: { 'X-Skip-401-Redirect': '1' } })
  return response.data?.data || response.data
}

export const updateBookReview = async (reviewId: string, payload: { content: string }) => {
  const response = await request.put(`/api/reviews/${reviewId}`, payload, { headers: { 'X-Skip-401-Redirect': '1' } })
  return response.data?.data || response.data
}

export const deleteBookReview = async (reviewId: string) => {
  const response = await request.delete(`/api/reviews/${reviewId}`, { headers: { 'X-Skip-401-Redirect': '1' } })
  return response.data?.data || response.data
}

export const toggleReviewLike = async (reviewId: string) => {
  const response = await request.post(`/api/reviews/${reviewId}/like`, {}, { headers: { 'X-Skip-401-Redirect': '1' } })
  return response.data?.data || response.data
}

export const toggleReviewFlower = async (reviewId: string) => {
  const response = await request.post(`/api/reviews/${reviewId}/flower`, {}, { headers: { 'X-Skip-401-Redirect': '1' } })
  return response.data?.data || response.data
}

export const toggleReviewDownvote = async (reviewId: string) => {
  const response = await request.post(`/api/reviews/${reviewId}/downvote`, {}, { headers: { 'X-Skip-401-Redirect': '1' } })
  return response.data?.data || response.data
}

export const toggleReviewDislike = async (reviewId: string) => {
  const response = await request.post(`/api/reviews/${reviewId}/dislike`, {}, { headers: { 'X-Skip-401-Redirect': '1' } })
  return response.data?.data || response.data
}

export const downloadUserReviews = async (userId: string) => {
  const url = `/api/users/${userId}/reviews/export`
  const response = await request.get(url, {
    responseType: 'blob',
    headers: { 'X-No-Retry': '1' }
  })
  return response
}
